import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription, debounceTime, of, switchMap, takeUntil } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BaseComponent } from '../../../core/components/base/base.component';
import { FeedbackService } from '../../../core/services/feedback.service';
import {
  PublicFormAnswerError,
  PublicFormLoad,
  PublicFormPractice,
  PublicFormState,
  PublicFormTemplate,
  PublicPatientFormService,
  readAnswerErrors,
  readPublicMessage,
  readPublicState,
} from '../../../core/services/public-patient-form.service';
import { DynamicFormRendererComponent }
  from '../../../shared/components/dynamic-form-renderer/dynamic-form-renderer.component';
import {
  FormAnswers,
  FormQuestion,
  FormSchema,
  FormSection,
} from '../../../shared/models/custom-form.model';

/**
 * The standalone patient-facing form page — `/patient-form/:token`.
 *
 * `tasks/patient-forms.md` §5 host 2. A SIBLING of `/patient-satisfaction-survey/:token` and
 * `/patient-invite/:token`, outside the `AuthGuard` block, because the one actor who needs this
 * page is a patient with no account.
 *
 * ── WHAT THIS COMPONENT IS, AND WHAT IT IS DELIBERATELY NOT ──────────────────────────────────
 *
 * It renders NO FIELDS. Every input on this page comes from `DynamicFormRendererComponent`
 * (§5, "one renderer, three hosts"), which owns field rendering, `showIf`, validation and the
 * answers map. What lives here is everything the renderer refuses to own: the route, the token,
 * the HTTP, the practice-branded shell, the paging chrome, autosave, submit, and the five screens.
 *
 * That split is the entire point of §5. A page that drew its own controls would be a second
 * implementation of the schema, and a second implementation is a second set of `showIf` bugs.
 *
 * ── FIVE SCREENS, NOT ONE ────────────────────────────────────────────────────────────────────
 *
 * `ok` · `not_found` · `completed` · `expired` · `revoked`, each with its own words. Collapsing
 * them is the defect `applyInviteLoadFailure` (`patient-invite-wizard.component.ts:250`) was
 * written to fix: a patient who simply re-opened a link they had already finished was told to
 * phone the practice. `completed` is checked BEFORE `expired` server-side, so a form filled in
 * months ago reads as done, never as expired — this page inherits that ordering by taking the
 * server's `status` verbatim rather than re-deriving one.
 *
 * The server's `message` is used AS IS wherever it sent one. It is written for a patient, it names
 * the one action that helps (contact the practice for a new link), and a client that paraphrased
 * it would drift out of step with the endpoint on the next wording change.
 *
 * ── WHY THE DRAFT IS SERVER-SIDE ─────────────────────────────────────────────────────────────
 *
 * A ten-page questionnaire is abandoned halfway as a matter of routine, and `localStorage` — what
 * the invite wizard uses — loses it the moment the patient moves from the phone that got the SMS
 * to the laptop they would rather type on. `PATCH` is debounced, never validated server-side, and
 * a draft never reaches a chart.
 *
 * ── WHY A FAILED SUBMIT NEVER CLEARS THE ANSWERS ─────────────────────────────────────────────
 *
 * This is a phone on a waiting-room network. `answers` lives on the component and is only ever
 * replaced by the renderer, never by a response — so a timeout leaves the patient looking at their
 * own answers with a Try again button. Retrying is safe because the endpoint claims before it
 * works: a second submit replays `{ alreadyCompleted: true }`, which this page treats as SUCCESS
 * (§14.3) rather than as an error, because that is what it means — their answers landed.
 *
 * ── NO COPY-LINK, NO RESEND ──────────────────────────────────────────────────────────────────
 *
 * §14.3 decision 2. The raw token is unrecoverable by construction and a resend rotates it, so
 * any link this page could offer is already dead. There is no affordance here and there cannot be.
 */

/** Which screen the patient is looking at. */
export type PatientFormPhase = 'loading' | 'form' | 'blocked' | 'error' | 'done';

/** The heading for each of the four refusals. The BODY is the server's message, verbatim. */
interface BlockedCopy {
  heading: string;
  icon: string;
  fallback: string;
}

const BLOCKED_COPY: Record<Exclude<PublicFormState, 'ok'>, BlockedCopy> = {
  completed: {
    heading: 'All done',
    icon: 'check_circle',
    fallback: 'You have already completed this form and it has been sent to the practice.'
      + ' There is nothing further you need to do.',
  },
  expired: {
    heading: 'This link has expired',
    icon: 'schedule',
    fallback: 'Please contact the medical practice that sent it to request a new link.',
  },
  revoked: {
    heading: 'This link is no longer active',
    icon: 'link_off',
    fallback: 'Please contact the medical practice that sent it to request a new link.',
  },
  not_found: {
    heading: 'We could not find this form',
    icon: 'help_outline',
    fallback: 'Please check the link you were sent, or contact the medical practice that sent it'
      + ' to request a new one.',
  },
};

@Component({
  selector: 'app-patient-form',
  templateUrl: './patient-form.component.html',
  styleUrls: ['./patient-form.component.scss'],
})
export class PatientFormComponent extends BaseComponent implements OnInit {

  /**
   * The renderer instance. Not `static`, because it lives behind `*ngIf="phase === 'form'"`.
   *
   * The page reads `isSectionValid` / `markAllTouched` off it rather than keeping a second opinion
   * on validity — §14.2 says the renderer's is the only one.
   */
  @ViewChild(DynamicFormRendererComponent) renderer?: DynamicFormRendererComponent;

  /**
   * How long the patient may keep typing before a draft is written.
   *
   * Long enough that a sentence is one PATCH rather than forty, short enough that a phone dying
   * mid-form loses a moment's typing rather than a page of it.
   */
  readonly autosaveDebounceMs = 1200;

  phase: PatientFormPhase = 'loading';

  token = '';

  /* ── Loaded content ──────────────────────────────────────────────────────────────────────── */

  schema: FormSchema | null = null;
  form: PublicFormTemplate | null = null;
  practice: PublicFormPractice | null = null;
  expiresAt: string | null = null;

  /** The live answers. Owned by the renderer; this page only holds and posts them. */
  answers: FormAnswers = {};

  /* ── Paging ──────────────────────────────────────────────────────────────────────────────── */

  /**
   * The visible sections, learned from `sectionsChange` (§14.2) rather than read off the schema.
   *
   * A section whose questions are all hidden by `showIf` is not in this list, so the progress
   * indicator counts steps the patient can actually reach — and it is recomputed as they answer,
   * which is why the current step is held as an ID and not an index.
   */
  sections: FormSection[] = [];

  /** `null` until the first `sectionsChange`, which is the one render that shows every section. */
  currentSectionId: string | null = null;

  /* ── Autosave ────────────────────────────────────────────────────────────────────────────── */

  saveState: 'idle' | 'pending' | 'saving' | 'saved' | 'failed' = 'idle';
  lastSavedAt: Date | null = null;

  /* ── Submit ──────────────────────────────────────────────────────────────────────────────── */

  isSubmitting = false;

  /** A retryable failure — a 500, a dropped connection. The answers are still on screen. */
  submitError: string | null = null;

  /** 400 `VALIDATION_FAILED`. The server validates against the pinned version and is the authority. */
  serverErrors: PublicFormAnswerError[] = [];

  /** The client-side "you have not finished this page yet" nudge. */
  stepError: string | null = null;

  /* ── The refusal / completion screens ────────────────────────────────────────────────────── */

  blockedState: PublicFormState | null = null;
  blockedHeading = '';
  blockedIcon = 'info';
  blockedMessage = '';

  /** The unexpected-failure screen. Retryable, unlike the four states. */
  loadErrorMessage = '';

  completedMessage = '';

  private readonly autosaveRequests$ = new Subject<FormAnswers>();

  /** What the server is known to hold, so an echo of the loaded draft never triggers a PATCH. */
  private savedSerialised = '';

  private submitSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private publicPatientFormService: PublicPatientFormService,
    private cd: ChangeDetectorRef,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.startAutosavePipeline();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.token = String(params['token'] || '');
      this.beginLoad();
    });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Loading — validate first, then load
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * VALIDATE, THEN LOAD, in that order.
   *
   * Two round trips where one would do, deliberately: `validate` is the endpoint whose whole job
   * is to distinguish the five states, and it returns them without touching the schema, the
   * practice or the draft. Going straight to `load` would work, but it would make the five-state
   * screen a side effect of a data fetch rather than the first thing this page establishes — and
   * that is exactly how four distinct outcomes end up collapsed into one error page.
   */
  beginLoad(): void {
    this.phase = 'loading';
    this.resetLoadedState();

    if (!this.token) {
      this.applyBlocked('not_found', null);
      return;
    }

    this.publicPatientFormService.validate(this.token).pipe(
      switchMap(() => this.publicPatientFormService.load(this.token)),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (data) => this.applyLoaded(data),
      error: (error: unknown) => this.applyLoadFailure(error),
    });
  }

  private resetLoadedState(): void {
    this.schema = null;
    this.form = null;
    this.practice = null;
    this.expiresAt = null;
    this.answers = {};
    this.sections = [];
    this.currentSectionId = null;
    this.saveState = 'idle';
    this.lastSavedAt = null;
    this.submitError = null;
    this.serverErrors = [];
    this.stepError = null;
    this.blockedState = null;
    this.loadErrorMessage = '';
    this.completedMessage = '';
  }

  private applyLoaded(data: PublicFormLoad): void {
    this.schema = data.schema || null;
    this.form = data.form;
    this.practice = data.practice;
    this.expiresAt = data.expiresAt;

    // The draft goes into the renderer RAW. §14.2 decision 6: a draft holding `'3'` where a scale
    // wants `3` must reach validation as `'3'`, because that is the value the server will see.
    this.answers = data.answers || {};
    this.savedSerialised = JSON.stringify(this.answers);

    this.phase = 'form';
    this.cd.detectChanges();
  }

  /**
   * One of the five states, or something else entirely.
   *
   * The four refusals are terminal — there is nothing the patient can retry, and offering a
   * Try again button on a revoked link is a button that will never work. Anything else (a 500, a
   * rate limit, a dead connection) IS retryable and gets a different screen that says so.
   */
  private applyLoadFailure(error: unknown): void {
    const state = readPublicState(error);
    if (state && state !== 'ok') {
      this.applyBlocked(state, readPublicMessage(error));
      return;
    }

    this.phase = 'error';
    this.loadErrorMessage = readPublicMessage(error)
      || 'We could not open this form. Please check your connection and try again.';
    this.cd.detectChanges();
  }

  private applyBlocked(state: Exclude<PublicFormState, 'ok'>, message: string | null): void {
    const copy = BLOCKED_COPY[state];
    this.phase = 'blocked';
    this.blockedState = state;
    this.blockedHeading = copy.heading;
    this.blockedIcon = copy.icon;
    this.blockedMessage = message || copy.fallback;
    this.cd.detectChanges();
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The renderer's three outputs
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * The visible sections changed — on first render, and whenever a `showIf` reveals or hides a
   * whole section.
   *
   * If the section the patient is standing on has just disappeared, they are moved to the nearest
   * one that still exists rather than left on a page that renders nothing.
   */
  onSectionsChange(sections: FormSection[]): void {
    const previousIndex = this.currentSectionIndex;
    this.sections = sections || [];

    if (this.sections.length === 0) {
      this.currentSectionId = null;
      return;
    }

    const stillThere = this.currentSectionId !== null
      && this.sections.some((section) => section.id === this.currentSectionId);
    if (stillThere) return;

    const fallbackIndex = previousIndex < 0
      ? 0
      : Math.min(previousIndex, this.sections.length - 1);
    this.currentSectionId = this.sections[fallbackIndex].id;
  }

  /**
   * The pruned answers, after every user change.
   *
   * A queued autosave is skipped when nothing actually differs from what the server holds, which
   * is what keeps the renderer's initial emission — an echo of the draft we just loaded — from
   * writing that same draft straight back.
   */
  onAnswersChange(answers: FormAnswers): void {
    this.answers = answers || {};

    // A change invalidates the server's opinion of the answers, so the old error list must go or
    // the patient is looking at a complaint about a question they have just fixed.
    this.serverErrors = [];
    this.stepError = null;

    if (JSON.stringify(this.answers) === this.savedSerialised) return;

    this.saveState = 'pending';
    this.autosaveRequests$.next(this.answers);
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Autosave
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * Debounced, and `switchMap`ped so a slow save is abandoned the moment a newer one is ready —
   * two PATCHes racing over one row would let an older answer set win.
   *
   * A failed autosave is NOT an error screen and NOT a toast. The patient can keep typing, the
   * next change tries again, and submit sends the whole map regardless — so the only thing a
   * failure costs is the resume-on-another-device guarantee, which is worth a quiet line of text
   * and nothing more.
   */
  private startAutosavePipeline(): void {
    this.autosaveRequests$.pipe(
      debounceTime(this.autosaveDebounceMs),
      switchMap((answers) => {
        this.saveState = 'saving';
        const serialised = JSON.stringify(answers);
        return this.publicPatientFormService.saveDraft(this.token, answers).pipe(
          map(() => ({ ok: true, serialised })),
          catchError(() => of({ ok: false, serialised })),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe((outcome) => {
      if (outcome.ok) {
        this.savedSerialised = outcome.serialised;
        this.saveState = 'saved';
        this.lastSavedAt = new Date();
      } else {
        this.saveState = 'failed';
      }
      this.cd.detectChanges();
    });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Paging
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  get currentSectionIndex(): number {
    if (this.currentSectionId === null) return -1;
    for (let i = 0; i < this.sections.length; i++) {
      if (this.sections[i].id === this.currentSectionId) return i;
    }
    return -1;
  }

  get currentSection(): FormSection | null {
    const index = this.currentSectionIndex;
    return index < 0 ? null : this.sections[index];
  }

  get isFirstSection(): boolean {
    return this.currentSectionIndex <= 0;
  }

  get isLastSection(): boolean {
    return this.sections.length === 0 || this.currentSectionIndex >= this.sections.length - 1;
  }

  /** 0–100. A ten-section form needs a bar that moves, not a step counter alone. */
  get progressPercent(): number {
    if (this.sections.length === 0) return 0;
    const index = this.currentSectionIndex < 0 ? 0 : this.currentSectionIndex;
    return Math.round(((index + 1) / this.sections.length) * 100);
  }

  /**
   * Next.
   *
   * `markAllTouched()` only when the section is NOT complete: it is how outstanding errors are
   * revealed, and revealing them on a section the patient has finished is scolding them for
   * nothing. `goToSection` then calls the renderer's `resetTouched()`, so a patient who was shown
   * errors on page 2 does not arrive on page 3 to a wall of red for questions they have not
   * reached. `submit()` deliberately does NOT reset: it reveals the errors and then takes the
   * patient to them, which only works if they are still showing when they get there.
   */
  next(): void {
    if (!this.validateCurrentSection()) return;
    const index = this.currentSectionIndex;
    if (index < 0 || index >= this.sections.length - 1) return;
    this.goToSection(this.sections[index + 1].id);
  }

  back(): void {
    const index = this.currentSectionIndex;
    if (index <= 0) return;
    this.goToSection(this.sections[index - 1].id);
  }

  goToSection(sectionId: string): void {
    this.currentSectionId = sectionId;
    this.stepError = null;
    if (this.renderer) this.renderer.resetTouched();
    this.scrollToTop();
  }

  private validateCurrentSection(): boolean {
    const sectionId = this.currentSectionId;
    if (!this.renderer || sectionId === null) return true;
    if (this.renderer.isSectionValid(sectionId)) {
      this.stepError = null;
      return true;
    }
    this.renderer.markAllTouched();
    this.stepError = 'Please finish the highlighted questions before continuing.';
    return false;
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Submit
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * Submit every answer, once.
   *
   * The whole form is checked, not just the section on screen — `isSectionValid` per section,
   * because `renderer.valid` reports only what THIS instance is paging to. A form that fails here
   * sends the patient to the first section that needs attention rather than telling them something
   * is wrong somewhere in ten pages.
   *
   * The server still validates, against the pinned version, and its answer wins: this check is a
   * courtesy that saves a round trip, not an authority.
   */
  submit(): void {
    if (this.isSubmitting) return;

    if (this.renderer) this.renderer.markAllTouched();

    const firstInvalid = this.firstInvalidSectionId();
    if (firstInvalid !== null) {
      this.stepError = 'Please finish the highlighted questions before sending your answers.';
      if (firstInvalid !== this.currentSectionId) {
        this.currentSectionId = firstInvalid;
        this.scrollToTop();
      }
      return;
    }

    this.isSubmitting = true;
    this.submitError = null;
    this.serverErrors = [];
    this.stepError = null;

    this.submitSub?.unsubscribe();
    this.submitSub = this.publicPatientFormService
      .submit(this.token, this.answers, this.signatureAnswer())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isSubmitting = false;
          // `alreadyCompleted` is a SUCCESS. It means their earlier submission landed and this is
          // a retry over a dropped response — the endpoint is idempotent by design.
          this.completedMessage = result.message
            || 'Thank you — your form has been sent to the practice.';
          this.phase = 'done';
          this.cd.detectChanges();
        },
        error: (error: unknown) => this.applySubmitFailure(error),
      });
  }

  /**
   * A submit that did not land.
   *
   * THE ANSWERS ARE NEVER TOUCHED HERE. Whatever went wrong, the patient is left looking at what
   * they typed with a way to send it again.
   */
  private applySubmitFailure(error: unknown): void {
    this.isSubmitting = false;

    const answerErrors = readAnswerErrors(error);
    if (answerErrors.length > 0) {
      this.serverErrors = answerErrors;
      this.submitError = readPublicMessage(error) || 'Some of your answers still need attention.';
      if (this.renderer) this.renderer.markAllTouched();
      const target = this.sectionIdForQuestion(answerErrors[0].questionId);
      if (target !== null && target !== this.currentSectionId) {
        this.currentSectionId = target;
      }
      this.scrollToTop();
      this.cd.detectChanges();
      return;
    }

    const state = readPublicState(error);
    if (state === 'completed') {
      // The row was completed between the load and this call — a second device, or a response we
      // never saw. Their answers are filed; this is the completion screen, not a failure.
      this.completedMessage = readPublicMessage(error) || BLOCKED_COPY.completed.fallback;
      this.phase = 'done';
      this.cd.detectChanges();
      return;
    }
    if (state && state !== 'ok') {
      this.applyBlocked(state, readPublicMessage(error));
      return;
    }

    this.submitError = readPublicMessage(error)
      || 'We could not send your answers. Please check your connection and try again.';
    this.cd.detectChanges();
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Helpers the template reads
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /** The first section with something outstanding on it, or `null` when the form is complete. */
  private firstInvalidSectionId(): string | null {
    if (!this.renderer) return null;
    for (const section of this.sections) {
      if (!this.renderer.isSectionValid(section.id)) return section.id;
    }
    return null;
  }

  /** Which section a question lives on, so a server error can move the patient to it. */
  private sectionIdForQuestion(questionId: string): string | null {
    if (!this.schema || !Array.isArray(this.schema.sections)) return null;
    for (const section of this.schema.sections) {
      const questions: FormQuestion[] = Array.isArray(section.questions) ? section.questions : [];
      for (const question of questions) {
        if (question.id === questionId) return section.id;
      }
    }
    return null;
  }

  /**
   * The question text a server error belongs to, so the banner reads "Date of birth — required"
   * rather than "q_7f3a — required". A question id is not something a patient can act on.
   */
  questionLabel(questionId: string): string {
    if (this.schema && Array.isArray(this.schema.sections)) {
      for (const section of this.schema.sections) {
        const questions: FormQuestion[] = Array.isArray(section.questions) ? section.questions : [];
        for (const question of questions) {
          if (question.id === questionId && typeof question.question === 'string') {
            return question.question;
          }
        }
      }
    }
    return 'This question';
  }

  /** Jump straight to the question a server error names. */
  goToError(questionId: string): void {
    const sectionId = this.sectionIdForQuestion(questionId);
    if (sectionId !== null) this.goToSection(sectionId);
  }

  /**
   * The signature the patient drew, if this schema asks for one.
   *
   * The endpoint keeps it in its own column, which nothing else on the public path fills. It is
   * the patient's own answer to their own form — not an id, and not something this page invented.
   */
  private signatureAnswer(): string | null {
    if (!this.schema || !Array.isArray(this.schema.sections)) return null;
    for (const section of this.schema.sections) {
      const questions: FormQuestion[] = Array.isArray(section.questions) ? section.questions : [];
      for (const question of questions) {
        if (question.type !== 'signature') continue;
        const value = this.answers[question.id];
        if (typeof value === 'string' && value !== '') return value;
      }
    }
    return null;
  }

  get hasPracticeContact(): boolean {
    return !!(this.practiceNumber || this.practicePhone || this.practiceEmail
      || this.practiceOpeningTimes);
  }

  /**
   * The branding fields as plain strings.
   *
   * Every one of these is a nullable encrypted column on `Group`, and `strictTemplates` will not
   * concatenate a `string | null` into an `href`. Flattening them here keeps the template free of
   * null handling and keeps "has the practice filled this in?" a single question with one answer.
   */
  get practiceName(): string {
    return (this.practice && this.practice.name) || 'Your medical practice';
  }

  get practiceAbout(): string {
    return (this.practice && this.practice.about) || '';
  }

  get practiceNumber(): string {
    return (this.practice && this.practice.practiceNumber) || '';
  }

  get practicePhone(): string {
    return (this.practice && this.practice.practicePhone) || '';
  }

  get practiceEmail(): string {
    return (this.practice && this.practice.practiceEmail) || '';
  }

  get practiceOpeningTimes(): string {
    return (this.practice && this.practice.practiceOpeningTimes) || '';
  }

  get formDescription(): string {
    return (this.form && this.form.description) || '';
  }

  get formTitle(): string {
    return (this.form && this.form.name) || 'Patient form';
  }

  trackSection(_index: number, section: FormSection): string {
    return section.id;
  }

  trackError(_index: number, error: PublicFormAnswerError): string {
    return error.questionId;
  }

  private scrollToTop(): void {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }
}
